/**
 * Custom-page renderer for connected services. Loads a fresh ticket
 * from the backend, then mounts the service's HTML inside a
 * cross-origin sandboxed iframe. The iframe self-reports its height
 * via postMessage so the embed feels native — no inner scrollbar, no
 * fixed height.
 *
 * Sandbox: `allow-same-origin allow-scripts allow-forms allow-popups`.
 * `allow-same-origin` is required for the service's JS to use its own
 * cookies; the cross-origin barrier between cloud-erp and the service
 * is what protects cloud-erp's session — sandbox flags are belt-and-
 * suspenders.
 *
 * Verb dispatch — when the iframe calls `cloudErpHost.<verb>(...)`, it
 * posts a message of shape
 * `{ source:'cloud-erp-page', type:'verb', verb, id, params }`. We
 * look up the verb in `HOST_VERBS_BY_NAME`, validate the params with
 * its Zod schema, run the parent-side handler, and reply with
 * `{ source:'cloud-erp-host', type:'verb-reply', id, ok, result|error }`.
 *
 * Adding a verb is one entry in the SDK registry + one case in the
 * dispatch switch below. The `host.js` proxies are auto-generated from
 * the registry, and the docs are autogenerated too — the registry is
 * the single source of truth.
 */

import { HOST_VERBS, type HostVerbName } from '@cloud/external-service-sdk/host'
import { DEFAULT_LOCALE } from '@cloud/shared/i18n/locale'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { AlertCircle, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast as sonnerToast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'
import { useUser } from '@/providers/user-provider'

export const Route = createFileRoute('/m/external/$connectionId/pages/$pageKey')({
  component: PageRenderer,
})

interface TicketResult {
  ticket: string
  pageUrl: string
  serviceOrigin: string
  expiresAt: number
}

interface ConfirmRequest {
  title: string
  body?: string
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
  resolve: (ok: boolean) => void
}

interface SheetRequest {
  title: string
  contentUrl: string
  widthPx?: number
  resolve: (out: { closed: boolean; result?: unknown }) => void
}

function PageRenderer() {
  const { connectionId, pageKey } = Route.useParams()
  const t = useTranslations()
  const navigate = useNavigate()
  const userCtx = useUser()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [iframeHeight, setIframeHeight] = useState<number | null>(null)
  const [iframeError, setIframeError] = useState(false)
  const [confirmReq, setConfirmReq] = useState<ConfirmRequest | null>(null)
  const [sheetReq, setSheetReq] = useState<SheetRequest | null>(null)

  const ticket = useMutation({
    ...orpc.external.pages.issueTicket.mutationOptions(),
  })

  const conn = useQuery({
    ...orpc.external.connections.get.queryOptions({ input: { id: connectionId } }),
    retry: false,
    throwOnError: false,
  })

  useEffect(() => {
    setIframeError(false)
    setIframeHeight(null)
    ticket.mutate({ connectionId, pageKey })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId, pageKey])

  const dispatchVerb = useCallback(
    async (verb: string, params: unknown, serviceOrigin: string): Promise<unknown> => {
      if (!(verb in HOST_VERBS)) throw new Error(`unknown_verb:${verb}`)
      const def = HOST_VERBS[verb as HostVerbName]
      const parsed = def.params.safeParse(params)
      if (!parsed.success) {
        throw new Error(`invalid_params:${parsed.error.issues[0]?.message ?? 'bad shape'}`)
      }
      switch (verb) {
        case 'toast': {
          const p = parsed.data as {
            kind: 'default' | 'success' | 'info' | 'warning' | 'error'
            message: string
            description?: string
            durationMs?: number
          }
          const opts: { description?: string; duration?: number } = {}
          if (p.description) opts.description = p.description
          if (p.durationMs) opts.duration = p.durationMs
          if (p.kind === 'success') sonnerToast.success(p.message, opts)
          else if (p.kind === 'error') sonnerToast.error(p.message, opts)
          else if (p.kind === 'warning') sonnerToast.warning(p.message, opts)
          else if (p.kind === 'info') sonnerToast.info(p.message, opts)
          else sonnerToast(p.message, opts)
          return undefined
        }
        case 'confirm': {
          const p = parsed.data as {
            title: string
            body?: string
            confirmLabel?: string
            cancelLabel?: string
            destructive?: boolean
          }
          return await new Promise<boolean>((resolve) => {
            setConfirmReq({ ...p, resolve })
          })
        }
        case 'navigate': {
          const p = parsed.data as { path: string }
          // Belt-and-suspenders — the Zod regex already rejects external
          // origins, but a paranoid second check covers anyone who
          // imports a misbuilt SDK.
          if (
            !p.path.startsWith('/') ||
            p.path.startsWith('//') ||
            /^https?:\/\//i.test(p.path)
          ) {
            throw new Error('invalid_path:external_origin_rejected')
          }
          await navigate({ to: p.path as never })
          return undefined
        }
        case 'requestUser': {
          const u = userCtx.current?.user
          if (!u) throw new Error('no_user_session')
          return {
            id: u.id,
            name: u.name ?? null,
            email: u.email ?? null,
            // Best-effort locale — browser language is the source of truth
            // for now; we don't yet persist a per-user locale field.
            locale: typeof navigator !== 'undefined' ? navigator.language : DEFAULT_LOCALE,
            role: u.role ?? null,
          }
        }
        case 'openSheet': {
          const p = parsed.data as { title: string; contentUrl: string; widthPx?: number }
          // The contentUrl MUST resolve to the same origin that minted
          // the calling iframe — otherwise service A could open a sheet
          // pointing at service B (or any external origin) and use the
          // user's session to fish for content.
          let absolute: string
          try {
            absolute = new URL(p.contentUrl, serviceOrigin).toString()
          } catch {
            throw new Error('invalid_content_url')
          }
          const absoluteOrigin = new URL(absolute).origin
          if (absoluteOrigin !== serviceOrigin) {
            throw new Error('cross_origin_sheet_rejected')
          }
          const sheet: SheetRequest = {
            title: p.title,
            contentUrl: absolute,
            resolve: () => {},
          }
          if (p.widthPx !== undefined) sheet.widthPx = p.widthPx
          return await new Promise<{ closed: boolean; result?: unknown }>((resolve) => {
            setSheetReq({ ...sheet, resolve })
          })
        }
        default:
          throw new Error(`no_dispatcher:${verb}`)
      }
    },
    [navigate, userCtx],
  )

  useEffect(() => {
    const data = ticket.data as TicketResult | undefined
    if (!data) return

    const onMessage = async (ev: MessageEvent) => {
      // Only trust messages from the registered service origin OR — for
      // sheet-close payloads — from the sheet's iframe (which is also
      // same-origin per openSheet's validation).
      if (ev.origin !== data.serviceOrigin) return
      const msg = ev.data as
        | {
            source?: string
            type?: string
            height?: number
            verb?: string
            id?: string
            params?: unknown
            result?: unknown
          }
        | null
        | undefined
      if (!msg || msg.source !== 'cloud-erp-page') return

      if (msg.type === 'resize' && typeof msg.height === 'number') {
        setIframeHeight(Math.max(120, Math.min(msg.height, 100_000)))
        return
      }

      if (msg.type === 'sheet-close') {
        setSheetReq((prev) => {
          if (!prev) return null
          prev.resolve({ closed: true, result: msg.result })
          return null
        })
        return
      }

      if (msg.type === 'verb' && msg.verb && msg.id) {
        const id = msg.id
        const verb = msg.verb
        try {
          const result = await dispatchVerb(verb, msg.params, data.serviceOrigin)
          iframeRef.current?.contentWindow?.postMessage(
            { source: 'cloud-erp-host', type: 'verb-reply', id, ok: true, result },
            data.serviceOrigin,
          )
        } catch (err) {
          iframeRef.current?.contentWindow?.postMessage(
            {
              source: 'cloud-erp-host',
              type: 'verb-reply',
              id,
              ok: false,
              error: err instanceof Error ? err.message : 'verb_failed',
            },
            data.serviceOrigin,
          )
        }
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [ticket.data, dispatchVerb])

  // Push the current theme to the iframe whenever it loads or theme changes.
  useEffect(() => {
    const data = ticket.data as TicketResult | undefined
    if (!data) return
    const push = () => {
      const theme = document.documentElement.classList.contains('dark') ? 'dark' : 'light'
      iframeRef.current?.contentWindow?.postMessage(
        { source: 'cloud-erp-host', type: 'theme', theme },
        data.serviceOrigin,
      )
    }
    const observer = new MutationObserver(push)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    push()
    return () => observer.disconnect()
  }, [ticket.data])

  if (conn.isLoading || ticket.isPending) {
    return (
      <div className="container mx-auto py-6 px-4 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[60vh] w-full" />
      </div>
    )
  }

  if (!conn.data || conn.data.status !== 'connected') {
    return (
      <div className="container mx-auto py-6 px-4">
        <h1 className="text-2xl font-bold">{t('externalRenderer.connectionUnavailable')}</h1>
        <p className="text-muted-foreground mt-2">
          {t('externalRenderer.connectionUnavailableHelp')}
        </p>
      </div>
    )
  }

  if (ticket.isError || !ticket.data) {
    return (
      <div className="container mx-auto py-6 px-4">
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 flex gap-3">
          <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
          <div>
            <p className="font-medium">Could not load page</p>
            <p className="text-sm text-muted-foreground mt-1">
              {ticket.error instanceof Error ? ticket.error.message : 'unknown_error'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  const data = ticket.data as TicketResult

  return (
    <div className="w-full">
      {iframeError ? (
        <div className="container mx-auto py-6 px-4">
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 flex gap-3">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <div>
              <p className="font-medium">Page failed to render</p>
              <p className="text-sm text-muted-foreground mt-1">
                The service refused to be embedded, or its X-Frame-Options /
                Content-Security-Policy doesn't allow this origin.
              </p>
            </div>
          </div>
        </div>
      ) : null}
      <iframe
        ref={iframeRef}
        title={pageKey}
        src={data.pageUrl}
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
        referrerPolicy="no-referrer"
        loading="eager"
        onError={() => setIframeError(true)}
        style={{
          width: '100%',
          height: iframeHeight ? `${iframeHeight}px` : 'calc(100vh - 4rem)',
          border: 'none',
          display: 'block',
          background: 'transparent',
        }}
      />

      <AlertDialog
        open={confirmReq !== null}
        onOpenChange={(open) => {
          if (!open && confirmReq) {
            confirmReq.resolve(false)
            setConfirmReq(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmReq?.title}</AlertDialogTitle>
            {confirmReq?.body ? (
              <AlertDialogDescription>{confirmReq.body}</AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                confirmReq?.resolve(false)
                setConfirmReq(null)
              }}
            >
              {confirmReq?.cancelLabel ?? 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              className={confirmReq?.destructive ? 'bg-destructive hover:bg-destructive/90' : ''}
              onClick={() => {
                confirmReq?.resolve(true)
                setConfirmReq(null)
              }}
            >
              {confirmReq?.confirmLabel ?? 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet
        open={sheetReq !== null}
        onOpenChange={(open) => {
          if (!open && sheetReq) {
            sheetReq.resolve({ closed: true })
            setSheetReq(null)
          }
        }}
      >
        <SheetContent
          side="right"
          className="p-0 flex flex-col"
          style={
            sheetReq?.widthPx
              ? { width: `${sheetReq.widthPx}px`, maxWidth: '90vw' }
              : { width: 'min(640px, 90vw)' }
          }
        >
          <SheetHeader className="px-4 py-3 border-b border-border flex flex-row items-center justify-between space-y-0">
            <SheetTitle className="text-base">{sheetReq?.title}</SheetTitle>
            <button
              type="button"
              aria-label="Close"
              className="rounded-sm opacity-70 hover:opacity-100 transition-opacity"
              onClick={() => {
                sheetReq?.resolve({ closed: true })
                setSheetReq(null)
              }}
            >
              <X className="h-4 w-4" />
            </button>
          </SheetHeader>
          {sheetReq ? (
            <iframe
              title={sheetReq.title}
              src={sheetReq.contentUrl}
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
              referrerPolicy="no-referrer"
              style={{
                flex: 1,
                width: '100%',
                border: 'none',
                background: 'transparent',
              }}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  )
}
